import React from "react";
import { iconMap, type IconName } from "./Icon.constants";
import { cx } from "@/utils/classnames";
import { Tooltip } from "@/components/TooltipV2";
import { Placement } from "@floating-ui/react-dom-interactions";

export type { IconName };

export type IconSize = "text" | "xxs" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl";

export interface IconProps extends React.ComponentProps<"svg"> {
  name: IconName;
  size?: IconSize;
  tooltipText?: string;
  tooltipPlacement?: Placement;
  tooltipStype?: React.CSSProperties;
}

export const IconSizes = {
  text: "min-h-[1em] min-w-[1em] h-[1em] w-[1em]",
  xxs: "min-h-2 min-w-2 h-2 w-2",
  xs: "min-h-2.5 min-w-2.5 h-2.5 w-2.5",
  sm: "min-h-3 min-w-3 h-3 w-3",
  md: "min-h-[14px] min-w-[14px] h-[14px] w-[14px]",
  lg: "min-h-5 min-w-5 h-5 w-5",
  xl: "min-h-6 min-w-6 h-6 w-6",
  "2xl": "min-h-8 min-w-8 h-8 w-8",
} satisfies Record<IconSize, string>;

export const Icon: React.FC<IconProps> = ({
  name,
  className,
  size = "md",
  tooltipText,
  tooltipPlacement,
  tooltipStype,
  ...rest
}) => {
  const IconComponent = iconMap[name];

  if (!IconComponent) {
    console.warn(`Icon "${name}" not found`);
    return null;
  }

  const baseComponent = (
    <IconComponent {...rest} className={cx(className, IconSizes[size])} aria-label={tooltipText} />
  );
  if (tooltipText) {
    return (
      <Tooltip content={tooltipText} placement={tooltipPlacement} style={tooltipStype}>
        <div className={cx(className, IconSizes[size])}>{baseComponent}</div>
      </Tooltip>
    );
  }

  return baseComponent;
};
